/**
 * Skeleton components for loading states
 * Prevents flash of "No data" messages during normal data loading
 */
import React from 'react'
import { cn } from '@agion/ui'

interface SkeletonProps {
  className?: string
}

/**
 * Loading skeleton for agent access check
 * Shows while agents are being loaded from the API
 */
export function AgentLoadingSkeleton({ className }: SkeletonProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center h-full text-center p-8", className)}>
      <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mb-6 animate-pulse">
        <div className="w-10 h-10 bg-slate-300 rounded-full"></div>
      </div>
      <div className="space-y-3 w-full max-w-md">
        <div className="h-6 bg-slate-200 rounded w-3/4 mx-auto animate-pulse"></div>
        <div className="h-4 bg-slate-200 rounded w-full mx-auto animate-pulse"></div>
        <div className="h-4 bg-slate-200 rounded w-5/6 mx-auto animate-pulse"></div>
      </div>
    </div>
  )
}

/**
 * Loading skeleton for individual agent item in a list
 */
export function AgentSkeleton({ className }: SkeletonProps) {
  return (
    <div className={cn("animate-pulse flex space-x-4 p-2", className)}>
      <div className="rounded-full bg-slate-200 h-10 w-10 flex-shrink-0"></div>
      <div className="flex-1 space-y-2 py-1">
        <div className="h-4 bg-slate-200 rounded w-3/4"></div>
        <div className="h-3 bg-slate-200 rounded w-1/2"></div>
      </div>
    </div>
  )
}

/**
 * Loading skeleton for mission list items
 * Shows in the sidebar while missions are being loaded
 */
export function MissionSkeleton({ className }: SkeletonProps) {
  return (
    <div className={cn("space-y-1 px-3", className)}>
      {[1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse py-2">
          <div className="h-4 bg-slate-200 rounded w-full"></div>
        </div>
      ))}
    </div>
  )
}

/**
 * Loading skeleton for sidebar sections
 * Generic skeleton for any section loading state
 */
export function SidebarSectionSkeleton({ className, count = 3 }: SkeletonProps & { count?: number }) {
  return (
    <div className={cn("space-y-1", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="animate-pulse px-3 py-2">
          <div className="h-4 bg-slate-200 rounded w-4/5"></div>
        </div>
      ))}
    </div>
  )
}
